import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, Loader2, ExternalLink, RotateCcw, Send, ShieldCheck, History, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { useDelivery, useDeliveryUpdater } from "@/lib/queries";
import { CHANGE_STATUS_LABELS, weekLabel, formatDateTime, formatAmount, diffFields, groupFields, weekRange } from "@/lib/format";
import { LoadingBlock, ErrorBlock, SpecDiffList, Section, Callout, Breadcrumbs } from "@/components/common/Blocks";
import { StateChip, toneForChangeStatus } from "@/components/common/Badges";
import { MessageWorkSurface, BasisPanel } from "@/components/delivery/WorkSurfaces";
import { ReleaseChecklist } from "@/components/delivery/Dialogs";

const STEPS = [
  { key: "registrer", label: "Registrer endring" },
  { key: "vurder", label: "Vurder konsekvenser" },
  { key: "grunnlag", label: "Oppdater grunnlag" },
  { key: "godkjenning", label: "Kundegodkjenning" },
  { key: "frigivelse", label: "Intern frigivelse" },
];

const stepFromStatus = (s) => ({ registrert: 1, vurdert: 2, grunnlag_oppdatert: 3, til_kundegodkjenning: 3, kundegodkjent: 4, endring_onsket: 1, frigitt: 5, avvist: 5 }[s] ?? 0);

const Stepper = ({ current, onSelect, maxReached, status }) => (
  <ol className="no-scrollbar mb-6 flex gap-x-6 overflow-x-auto border-b" data-testid="change-stepper">
    {STEPS.map((s, i) => {
      const done = i < maxReached || status === "frigitt";
      const active = i === current;
      const reachable = i <= maxReached;
      return (
        <li key={s.key} className="shrink-0">
          <button
            type="button"
            disabled={!reachable}
            onClick={() => onSelect(i)}
            className={`-mb-px flex items-center gap-2 border-b-2 pb-2.5 pt-1 text-[14px] transition-colors ${active ? "border-primary font-semibold text-foreground" : done ? "border-transparent text-foreground hover:text-primary" : "border-transparent text-muted-foreground"} disabled:cursor-not-allowed`}
            data-testid={`change-step-${s.key}`}
            aria-current={active ? "step" : undefined}
          >
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11.5px] tabular ${done && !active ? "bg-foreground/80 text-background" : active ? "bg-primary text-primary-foreground" : "border text-muted-foreground"}`}>{done && !active ? <Check className="h-3 w-3" strokeWidth={3} /> : i + 1}</span>
            {s.label}
          </button>
        </li>
      );
    })}
  </ol>
);

const Note = ({ children, testId = "explain-box" }) => <p className="t-meta leading-relaxed" data-testid={testId}>{children}</p>;

/* ------------------------------------------------------------------ Shared summary */
const ItemsSummary = ({ cr }) => (
  <Section title={cr.title} testId="change-summary" right={<StateChip tone={toneForChangeStatus(cr.status)} strong>{CHANGE_STATUS_LABELS[cr.status]}</StateChip>} description={`Endring ${cr.number}`}>
    <dl className="divide-y border-t text-[14.5px]">
      {cr.items.map((i) => (
        <div key={i.id} className="grid grid-cols-[minmax(110px,36%)_1fr] gap-3 py-2" data-testid={`summary-item-${i.id}`}>
          <dt className="t-meta pt-0.5">{i.label}</dt>
          <dd className="flex flex-wrap items-center gap-2"><span className="diff-from">{i.from || "–"}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /><span className="diff-to">{i.to}</span>{i.origin === "ki" && <span className="t-meta">KI-forslag, bekreftet</span>}</dd>
        </div>
      ))}
    </dl>
    {cr.customer_questions?.length > 0 && <div className="mt-3 text-[14.5px]"><p className="t-label mb-1">Kunden spør</p><ul className="list-disc space-y-0.5 pl-5">{cr.customer_questions.map((q, i) => <li key={i}>{q.question}</li>)}</ul></div>}
    {cr.customer_decision?.decision === "endring_onsket" && (
      <Callout tone="rest" className="mt-4" title={`Kunden ba om endring ${formatDateTime(cr.customer_decision.at)}`} testId="customer-requested-change-note">«{cr.customer_decision.comment}»</Callout>
    )}
  </Section>
);

/* Defined outside the step so inputs keep focus between re-renders. */
const Radio = ({ value, label, name, current, readOnly }) => (
  <label className={`flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-md border px-3 text-[14.5px] transition-colors ${current === value ? "border-primary bg-[hsl(var(--accent))] font-medium" : "hover:bg-secondary"} ${readOnly ? "cursor-default" : ""}`}>
    <RadioGroupItem value={value} id={`${name}-${value}`} data-testid={`${name}-${value}`} /> <span>{label}</span>
  </label>
);

const Field = ({ title, hint, children }) => (
  <fieldset className="space-y-2.5">
    <legend className="text-[14.5px] font-medium">{title} {hint && <span className="t-meta ml-1 font-normal">{hint}</span>}</legend>
    {children}
  </fieldset>
);

/* ------------------------------------------------------------------ Steg 2 */
const AssessStep = ({ d, cr }) => {
  const update = useDeliveryUpdater();
  const a = cr.assessment || {};
  const readOnly = !["registrert", "vurdert", "endring_onsket"].includes(cr.status);
  const [price, setPrice] = useState(a.price || "ukjent");
  const [priceAmount, setPriceAmount] = useState(a.price_amount ?? "");
  const [priceNote, setPriceNote] = useState(a.price_note || "");
  const [delivery, setDelivery] = useState(a.delivery || "ukjent");
  const [newWeek, setNewWeek] = useState(a.new_agreed_week || "");
  const [deliveryNote, setDeliveryNote] = useState(a.delivery_note || "");
  const [drawing, setDrawing] = useState(a.drawing || "ingen");
  const [drawingNote, setDrawingNote] = useState(a.drawing_note || "");
  const [productionNote, setProductionNote] = useState(a.production_note || "");
  const [ack, setAck] = useState(a.production_ack_by || "");
  const [other, setOther] = useState(a.other_note || "");
  const [reply, setReply] = useState(a.customer_reply || "");
  const [by, setBy] = useState(a.assessed_by || d.responsible);
  const [busy, setBusy] = useState(false);
  const prodStarted = ["startet", "ferdig"].includes(d.production.status);
  const weeks = useMemo(() => weekRange(0, 14), []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/deliveries/${d.id}/changes/${cr.id}/assessment`, {
        price, price_amount: price === "tillegg" || price === "fradrag" ? (priceAmount === "" ? null : Number(priceAmount)) : null, price_note: priceNote || null,
        delivery, new_agreed_week: delivery === "ny_uke" ? newWeek || null : null, delivery_note: deliveryNote || null,
        drawing, drawing_note: drawingNote || null, production_note: productionNote || null, production_ack_by: ack || null, other_note: other || null, customer_reply: reply || null, assessed_by: by,
      });
      update(data);
      toast.success("Konsekvens vurdert", { description: "Neste steg: oppdater grunnlaget med ny revisjon." });
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke lagre vurderingen"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-8 lg:grid-cols-12">
      <div className="space-y-6 lg:col-span-5">
        <ItemsSummary cr={cr} />
        <Note>Vurderingen gjøres manuelt. Systemet finner ikke på konsekvenser – ansvarlig registrerer om endringen påvirker pris, leveringsuke, tegning eller produksjon, og hva som skal formidles til kunden.</Note>
      </div>
      <div className="lg:col-span-7">
        <Section title="Konsekvensvurdering" testId="assessment-form" right={readOnly ? "Låst – vurdert" : null}>
          <div className="space-y-6">
            <Field title="Pris">
              <RadioGroup value={price} onValueChange={setPrice} disabled={readOnly} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Radio value="ingen" label="Ingen endring" name="price" current={price} readOnly={readOnly} /><Radio value="tillegg" label="Tillegg" name="price" current={price} readOnly={readOnly} /><Radio value="fradrag" label="Fradrag" name="price" current={price} readOnly={readOnly} /><Radio value="ukjent" label="Må vurderes" name="price" current={price} readOnly={readOnly} />
              </RadioGroup>
              {(price === "tillegg" || price === "fradrag") && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <div><Label htmlFor="pa" className="t-meta">Beløp (NOK eks. mva)</Label><Input id="pa" type="number" value={priceAmount} onChange={(e) => setPriceAmount(e.target.value)} disabled={readOnly} className="mt-1 h-10" data-testid="price-amount-input" /></div>
                  <div><Label htmlFor="pn" className="t-meta">Grunnlag (internt)</Label><Input id="pn" value={priceNote} onChange={(e) => setPriceNote(e.target.value)} disabled={readOnly} className="mt-1 h-10" data-testid="price-note-input" /></div>
                </div>
              )}
            </Field>
            <Field title="Leveringsuke" hint={`avtalt: ${weekLabel(d.dates.agreed_delivery_week).toLowerCase()}`}>
              <RadioGroup value={delivery} onValueChange={setDelivery} disabled={readOnly} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Radio value="uendret" label="Avtalt uke holder" name="delivery" current={delivery} readOnly={readOnly} /><Radio value="ny_uke" label="Ny leveringsuke" name="delivery" current={delivery} readOnly={readOnly} /><Radio value="ukjent" label="Leveringstid må vurderes" name="delivery" current={delivery} readOnly={readOnly} />
              </RadioGroup>
              {delivery === "ny_uke" && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select value={newWeek} onValueChange={setNewWeek} disabled={readOnly}>
                    <SelectTrigger data-testid="new-week-select" className="h-10"><SelectValue placeholder="Velg uke" /></SelectTrigger>
                    <SelectContent>{weeks.map((w) => <SelectItem key={w} value={w}>{weekLabel(w)} ({w.split("-W")[0]})</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="Begrunnelse" disabled={readOnly} className="h-10" />
                </div>
              )}
            </Field>
            <Field title="Tegning">
              <RadioGroup value={drawing} onValueChange={setDrawing} disabled={readOnly} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Radio value="ingen" label="Ingen ny tegningsrevisjon (påvirker ikke geometri)" name="drawing" current={drawing} readOnly={readOnly} /><Radio value="ny_revisjon" label="Krever ny tegningsrevisjon" name="drawing" current={drawing} readOnly={readOnly} />
              </RadioGroup>
              <Input value={drawingNote} onChange={(e) => setDrawingNote(e.target.value)} placeholder="Merknad, f.eks. «Overflate påvirker ikke geometri»" disabled={readOnly} className="h-10" data-testid="drawing-note-input" />
            </Field>
            <Field title="Produksjon">
              {prodStarted ? (
                <Callout tone="waiting" title="Produksjonen er startet" testId="production-started-warning">
                  Ansvarlig må vurdere om endringen fortsatt er gjennomførbar. Produksjon må bekrefte før frigivelse. En statusendring stopper ingen maskin.
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <Input value={ack} onChange={(e) => setAck(e.target.value)} placeholder="Bekreftet av (produksjon)" disabled={readOnly} className="h-10" data-testid="production-ack-input" />
                    <Input value={productionNote} onChange={(e) => setProductionNote(e.target.value)} placeholder="Merknad" disabled={readOnly} className="h-10" />
                  </div>
                </Callout>
              ) : (
                <p className="text-[14.5px] text-muted-foreground" data-testid="production-not-started">Produksjon er ikke startet (planlagt {weekLabel(d.production.planned_week).toLowerCase()}). Endringen kan tas inn før oppstart.</p>
              )}
            </Field>
            <div className="space-y-1.5"><Label htmlFor="other" className="text-[14.5px] font-medium">Annet grunnlag som påvirkes</Label><Input id="other" value={other} onChange={(e) => setOther(e.target.value)} placeholder="F.eks. materialbestilling, fargeprøve" disabled={readOnly} className="h-10" /></div>
            <div className="space-y-1.5">
              <Label htmlFor="reply" className="text-[14.5px] font-medium">Svar til kunden <span className="t-meta font-normal">(vises i kundevisningen)</span></Label>
              <Textarea id="reply" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} disabled={readOnly} placeholder="Kort og konkret: hva endres, hva koster det, og holder leveringsuken?" data-testid="customer-reply-input" />
            </div>
            <div className="grid gap-2 sm:grid-cols-2"><div><Label htmlFor="by" className="t-meta">Vurdert av</Label><Input id="by" value={by} onChange={(e) => setBy(e.target.value)} disabled={readOnly} className="mt-1 h-10" /></div></div>
            {!readOnly && <div className="flex justify-end border-t pt-4"><Button onClick={save} disabled={busy} data-testid="assessment-save-button">{busy && <Loader2 className="animate-spin" />} Lagre vurdering</Button></div>}
            {readOnly && <Note>Vurdert {formatDateTime(a.assessed_at)} av {a.assessed_by}.</Note>}
          </div>
        </Section>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ Steg 3 */
const RevisionStep = ({ d, cr }) => {
  const update = useDeliveryUpdater();
  const base = d.computed.current_spec_revision || d.spec_revisions.find((r) => r.id === d.spec_revisions[0]?.id);
  const existing = cr.new_spec_revision_id ? d.spec_revisions.find((r) => r.id === cr.new_spec_revision_id) : null;
  const editable = ["vurdert", "grunnlag_oppdatert", "endring_onsket"].includes(cr.status);
  const initial = useMemo(() => {
    if (existing) return existing.fields.map((f) => ({ ...f }));
    return (base?.fields || []).map((f) => {
      const it = cr.items.find((i) => i.field_key === f.key);
      return it ? { ...f, value: it.to } : { ...f };
    });
  }, [existing, base, cr.items]);
  const [fields, setFields] = useState(initial);
  const [note, setNote] = useState(existing?.note || `Oppdatert etter endring ${cr.number}: ${cr.title}`);
  const [newDrawing, setNewDrawing] = useState(cr.assessment?.drawing === "ny_revisjon");
  const [drawingNote, setDrawingNote] = useState(cr.assessment?.drawing_note || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setFields(initial), [initial]);
  const diff = diffFields(base?.fields || [], fields);
  const unmatched = cr.items.filter((i) => !i.field_key || !(base?.fields || []).some((f) => f.key === i.field_key));

  const save = async () => {
    if (!diff.length) { toast.error("Ingen felt er endret."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/deliveries/${d.id}/changes/${cr.id}/revision`, { fields, note, create_drawing_revision: newDrawing, drawing_note: drawingNote || null });
      update(data);
      toast.success("Ny spesifikasjonsrevisjon opprettet", { description: `Rev. ${base?.code || ""} beholdes som gjeldende til den nye er godkjent og frigitt.` });
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke opprette revisjonen"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-8 lg:grid-cols-12">
      <div className="space-y-8 lg:col-span-5">
        <ItemsSummary cr={cr} />
        <Section title={`Endringer fra rev. ${base?.code} ${existing ? `til rev. ${existing.code}` : "(forhåndsvisning)"}`} testId="revision-diff-preview">
          <SpecDiffList diff={diff} testId="revision-diff-list" />
          {unmatched.length > 0 && <p className="mt-3 text-[14px]" style={{ color: "hsl(var(--state-waiting))" }}>{unmatched.length} endringspunkt matcher ikke et felt – legg det inn manuelt i riktig felt.</p>}
        </Section>
        <Section title="Tegning" testId="revision-drawing">
          <Note>En ny tegningsrevisjon lages bare når geometrien endres. Overflate og farge oppdaterer spesifikasjonen, ikke tegningen.</Note>
          <label className="mt-3 flex min-h-[40px] cursor-pointer items-center gap-3 text-[14.5px]">
            <Checkbox checked={newDrawing} onCheckedChange={(v) => setNewDrawing(!!v)} disabled={!editable} data-testid="create-drawing-revision-checkbox" />
            <span>Opprett ny tegningsrevisjon av gjeldende tegninger{cr.assessment?.drawing === "ny_revisjon" ? " (anbefalt i vurderingen)" : ""}</span>
          </label>
          {newDrawing && <Input className="mt-2 h-10" value={drawingNote} onChange={(e) => setDrawingNote(e.target.value)} placeholder="Hva endres i tegningen?" disabled={!editable} />}
        </Section>
      </div>
      <div className="lg:col-span-7">
        <Section title={`Ny spesifikasjon ${existing ? `rev. ${existing.code}` : ""}`} testId="revision-form" right={existing ? <StateChip tone="ready" strong>Opprettet</StateChip> : <StateChip tone="neutral">Utkast</StateChip>}>
          <div className="space-y-5">
            {groupFields(fields).map(([g, fs]) => (
              <div key={g}>
                <p className="t-label mb-2">{g}</p>
                <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                  {fs.map((f) => {
                    const changed = diff.some((x) => x.key === f.key);
                    return (
                      <div key={f.key} className={changed ? "field-changed pl-3" : ""}>
                        <Label htmlFor={`f-${f.key}`} className="t-meta">{f.label}{changed && <span className="ml-1.5 font-medium" style={{ color: "hsl(var(--state-waiting))" }}>endret</span>}</Label>
                        <Input id={`f-${f.key}`} value={f.value} disabled={!editable} onChange={(e) => setFields(fields.map((x) => (x.key === f.key ? { ...x, value: e.target.value } : x)))} data-testid={`revision-field-${f.key}`} className="mt-1 h-10" />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="space-y-1.5"><Label htmlFor="rev-note">Merknad til revisjonen</Label><Input id="rev-note" value={note} onChange={(e) => setNote(e.target.value)} disabled={!editable} className="h-10" data-testid="revision-note-input" /></div>
          </div>
          {editable && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <Note>{existing ? "Du kan justere og lagre på nytt før den sendes til kunden." : "Gammel revisjon beholdes. Ny revisjon er et utkast til den er godkjent og frigitt."}</Note>
              <Button onClick={save} disabled={busy || !diff.length} data-testid="revision-save-button">{busy && <Loader2 className="animate-spin" />} {existing ? "Oppdater revisjon" : "Opprett ny revisjon"}</Button>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ Steg 4 */
const ApprovalStep = ({ d, cr, goTo }) => {
  const update = useDeliveryUpdater();
  const ar = d.approval_requests.find((a) => a.id === cr.approval_request_id);
  const [recipient, setRecipient] = useState(d.roles?.orderer?.name || d.customer_name);
  const [message, setMessage] = useState(cr.assessment?.customer_reply || "");
  const [busy, setBusy] = useState(false);
  const rev = d.spec_revisions.find((r) => r.id === cr.new_spec_revision_id);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deliveries/${d.id}/changes/${cr.id}/send-approval`, { recipient, message_to_customer: message, sent_by: d.responsible });
      update(data);
      toast.success("Sendt til kundegodkjenning (simulert)", { description: "Ingen e-post er sendt. Åpne kundevisningen for å svare som kunde." });
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke sende til kundegodkjenning"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-8 lg:grid-cols-12">
      <div className="space-y-6 lg:col-span-5"><ItemsSummary cr={cr} /></div>
      <div className="space-y-6 lg:col-span-7">
        {cr.status === "grunnlag_oppdatert" && (
          <Section title={`Send rev. ${rev?.code} til kundegodkjenning`} testId="send-approval-form">
            <div className="space-y-4">
              <Note>Kunden får en begrenset kundevisning som viser nøyaktig hva som er endret, pris- og leveringskonsekvens, og to valg: godkjenn eller be om endring. Ingen e-post sendes i prototypen.</Note>
              <div className="space-y-1.5"><Label htmlFor="rec">Mottaker</Label><Input id="rec" value={recipient} onChange={(e) => setRecipient(e.target.value)} className="h-10" data-testid="approval-recipient-input" /></div>
              <div className="space-y-1.5"><Label htmlFor="msg">Melding til kunden</Label><Textarea id="msg" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="approval-message-input" /></div>
              <div className="flex justify-end border-t pt-4"><Button onClick={send} disabled={busy} data-testid="change-wizard-send-approval-button">{busy ? <Loader2 className="animate-spin" /> : <Send />} Send til kundegodkjenning</Button></div>
            </div>
          </Section>
        )}
        {cr.status === "til_kundegodkjenning" && ar && (
          <Callout tone="waiting" title="Venter på kundegodkjenning" testId="approval-waiting"
            action={<Button asChild size="sm"><Link to={`/kunde/${ar.token}`} target="_blank" data-testid="open-customer-view-link"><ExternalLink /> Åpne kundevisning (simulert kunde)</Link></Button>}>
            Sendt {formatDateTime(ar.sent_at)} til {ar.recipient}. I en reell løsning ville kunden fått lenken på e-post. Her åpner du kundevisningen selv og svarer som kunde.
            <p className="t-meta mt-1">Når kunden har svart, oppdateres denne siden når du kommer tilbake hit.</p>
          </Callout>
        )}
        {["kundegodkjent", "frigitt"].includes(cr.status) && cr.customer_decision && (
          <Callout tone="released" title="Kundegodkjent" testId="approval-decision"
            action={cr.status === "kundegodkjent" ? <Button size="sm" onClick={() => goTo(4)} data-testid="go-to-release-button">Gå til intern frigivelse <ArrowRight /></Button> : null}>
            {cr.customer_decision.name} godkjente rev. {rev?.code} {formatDateTime(cr.customer_decision.at)}.{cr.customer_decision.comment ? ` «${cr.customer_decision.comment}»` : ""}
            <p className="t-meta mt-1">Kundegodkjenning er ikke intern frigivelse. Grunnlaget gjelder først etter teknisk kontroll i neste steg.</p>
          </Callout>
        )}
        {cr.status === "endring_onsket" && (
          <Callout tone="rest" title="Kunden ber om endring" testId="approval-change-requested"
            action={<Button size="sm" variant="outline" onClick={() => goTo(1)} data-testid="reassess-button"><RotateCcw /> Vurder på nytt</Button>}>
            «{cr.customer_decision?.comment}»
            <p className="t-meta mt-1">Vurder på nytt, oppdater grunnlaget og send til godkjenning igjen. Gjeldende revisjon er uendret.</p>
          </Callout>
        )}
        {["registrert", "vurdert"].includes(cr.status) && <Note testId="locked-note">Grunnlaget må oppdateres før det kan sendes til kunden.</Note>}
        {ar && cr.status !== "til_kundegodkjenning" && <p className="t-meta">Kundevisning: <Link to={`/kunde/${ar.token}`} target="_blank" className="link">åpne (lesevisning)</Link></p>}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ Steg 5 */
const ReleaseStep = ({ d, cr }) => {
  const update = useDeliveryUpdater();
  const [checks, setChecks] = useState({});
  const [by, setBy] = useState(d.technical_responsible || "Sindre Tveit");
  const [note, setNote] = useState("");
  const [ack, setAck] = useState(cr.assessment?.production_ack_by || "");
  const [busy, setBusy] = useState(false);
  const prodStarted = ["startet", "ferdig"].includes(d.production.status);
  const rev = d.spec_revisions.find((r) => r.id === cr.new_spec_revision_id);
  const prev = rev?.based_on ? d.spec_revisions.find((r) => r.id === rev.based_on) : null;
  const ok = checks.spec && checks.drawing && checks.measurements && (!prodStarted || ack.trim());

  const release = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deliveries/${d.id}/changes/${cr.id}/release`, { reviewed_by: by, note, checks, production_ack_by: ack || null });
      update(data);
      toast.success(`Rev. ${rev?.code} er frigitt`, { description: "Grunnlaget er nå gjeldende. Eldre revisjoner er beholdt i historikken." });
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke frigi grunnlaget"));
    } finally {
      setBusy(false);
    }
  };

  const a = cr.assessment;

  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-8 lg:grid-cols-12">
      <div className="space-y-8 lg:col-span-5">
        <ItemsSummary cr={cr} />
        {a && (
          <Section title="Vurdert konsekvens" testId="release-assessment-summary">
            <dl className="divide-y border-t text-[14.5px]">
              <div className="grid grid-cols-[110px_1fr] gap-3 py-2"><dt className="t-meta">Pris</dt><dd>{a.price === "ingen" ? "Ingen endring" : a.price === "ukjent" ? "Må vurderes" : `${a.price === "tillegg" ? "Tillegg" : "Fradrag"} ${formatAmount(a.price_amount)}`}</dd></div>
              <div className="grid grid-cols-[110px_1fr] gap-3 py-2"><dt className="t-meta">Levering</dt><dd>{a.delivery === "uendret" ? `${weekLabel(d.dates.agreed_delivery_week)} holder` : a.delivery === "ny_uke" ? `Ny uke: ${weekLabel(a.new_agreed_week).toLowerCase()}` : "Leveringstid må vurderes"}</dd></div>
              <div className="grid grid-cols-[110px_1fr] gap-3 py-2"><dt className="t-meta">Tegning</dt><dd>{a.drawing === "ny_revisjon" ? "Ny revisjon" : "Ingen endring"}</dd></div>
              <div className="grid grid-cols-[110px_1fr] gap-3 py-2"><dt className="t-meta">Produksjon</dt><dd>{a.production === "startet" ? "Startet – krever bekreftelse" : "Ikke startet"}</dd></div>
            </dl>
          </Section>
        )}
      </div>
      <div className="space-y-6 lg:col-span-7">
        {cr.status === "kundegodkjent" && (
          <Section title={`Intern teknisk kontroll – frigi rev. ${rev?.code}`} testId="release-form">
            <div className="space-y-5">
              <Note>Kunden har godkjent. Nå kontrolleres grunnlaget teknisk før det blir gjeldende for produksjon. Rev. {prev?.code} erstattes, men beholdes i historikken.</Note>
              <ReleaseChecklist checks={checks} setChecks={setChecks} productionStarted={prodStarted} ack={ack} setAck={setAck} />
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label htmlFor="rb">Kontrollert av</Label><Input id="rb" value={by} onChange={(e) => setBy(e.target.value)} className="h-10" data-testid="release-reviewed-by" /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="rn">Merknad</Label><Textarea id="rn" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="release-note" /></div>
              </div>
              <div className="flex items-center justify-between gap-3 border-t pt-4">
                <Note>{ok ? "Alle kontrollpunkter er bekreftet." : "Bekreft alle kontrollpunkter for å frigi."}</Note>
                <Button onClick={release} disabled={!ok || busy} data-testid="internal-release-confirm-button">{busy ? <Loader2 className="animate-spin" /> : <ShieldCheck />} Frigi grunnlaget</Button>
              </div>
            </div>
          </Section>
        )}
        {cr.status === "frigitt" && (
          <Callout tone="released" title={`Rev. ${rev?.code} er frigitt og gjeldende`} testId="release-done"
            action={<><Button asChild size="sm" variant="outline"><Link to={`/leveranser/${d.id}?tab=historikk`} data-testid="go-to-history-link"><History /> Se historikk</Link></Button><Button asChild size="sm" variant="ghost"><Link to={`/leveranser/${d.id}?tab=spesifikasjon`}>Se spesifikasjon</Link></Button></>}>
            Frigitt {formatDateTime(cr.released_at)} av {cr.technical_review?.reviewed_by}.{cr.technical_review?.note ? ` «${cr.technical_review.note}»` : ""}
            <p className="t-meta mt-1">Kundebeskjed → registrert endring → manuell vurdering → ny revisjon → kundegodkjenning → intern frigivelse.</p>
          </Callout>
        )}
        {cr.status === "avvist" && <Callout tone="neutral" title="Avsluttet uten gjennomføring">{cr.rejected?.reason}</Callout>}
        {!["kundegodkjent", "frigitt", "avvist"].includes(cr.status) && (
          <Note testId="locked-note">Intern frigivelse krever at kunden har godkjent det nye grunnlaget først. Kundegodkjenning alene gjør ikke leveransen produksjonsklar.</Note>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ Page */
export default function EndringPage() {
  const { id, changeId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: d, isLoading, isError, refetch } = useDelivery(id);
  const [view, setView] = useState(null);
  const update = useDeliveryUpdater();
  const cr = d?.change_requests.find((c) => c.id === changeId) || null;
  const status = cr?.status;
  const auto = cr ? stepFromStatus(status) : 0;
  const current = Math.min(view ?? auto, 4);
  const maxReached = Math.min(auto, 4);
  useEffect(() => { setView(null); }, [status]);

  if (isLoading) return <div className="page"><LoadingBlock rows={6} /></div>;
  if (isError || !d) return <div className="page"><ErrorBlock message="Fant ikke leveransen." onRetry={refetch} /></div>;
  if (changeId && !cr) return <div className="page"><ErrorBlock message="Fant ikke endringen." /></div>;

  const reject = async () => {
    const reason = window.prompt("Hvorfor avsluttes endringen uten gjennomføring?");
    if (!reason) return;
    try {
      const { data } = await api.post(`/deliveries/${d.id}/changes/${cr.id}/reject`, { reason });
      update(data);
      toast.success("Endringen er avsluttet");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="page" data-testid="change-page">
      <Breadcrumbs items={[{ label: "Leveranser", to: "/leveranser" }, { label: d.project_name, to: `/leveranser/${d.id}`, testId: "back-to-delivery" }, { label: cr ? `Endring ${cr.number}` : "Ny endring" }]} />
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <h1 className="h-page">{cr ? cr.title : "Registrer endring"}</h1>
          <p className="t-meta mt-1">{d.number} · {d.project_name} · gjeldende spesifikasjon rev. {d.computed.current_spec_revision?.code || "–"} endres ikke før siste steg</p>
        </div>
        <div className="flex items-center gap-3">
          {cr && <StateChip tone={toneForChangeStatus(cr.status)} strong>{CHANGE_STATUS_LABELS[cr.status]}</StateChip>}
          {cr && !["frigitt", "avvist"].includes(cr.status) && <Button variant="ghost" size="sm" onClick={reject} data-testid="change-reject-button">Avslutt uten gjennomføring</Button>}
        </div>
      </div>
      <Stepper current={current} onSelect={(i) => setView(i)} maxReached={maxReached} status={status} />
      <div key={current}>
        {current === 0 && (cr ? <div className="lg:max-w-[60%]"><ItemsSummary cr={cr} /></div> : (
          <div className="grid grid-cols-1 gap-x-12 gap-y-10 lg:grid-cols-12">
            <div className="lg:col-span-7 xl:col-span-8"><MessageWorkSurface d={d} messageId={params.get("melding")} /></div>
            <div className="lg:col-span-5 xl:col-span-4"><BasisPanel d={d} onTab={(t) => navigate(`/leveranser/${d.id}?tab=${t}`)} /></div>
          </div>
        ))}
        {current === 1 && cr && <AssessStep d={d} cr={cr} />}
        {current === 2 && cr && <RevisionStep d={d} cr={cr} />}
        {current === 3 && cr && <ApprovalStep d={d} cr={cr} goTo={setView} />}
        {current === 4 && cr && <ReleaseStep d={d} cr={cr} />}
      </div>
    </div>
  );
}
